'use client';
import useTranslation from '@components/Translation/useTranslation';
import { UnitOfMeasureEnum } from '@enums/recipe-enums';
import { IngredientSummaryProps } from '@repo/ui/src/types/recipes';
import { StateSetterFunction } from '@repo/ui/src/types/helpers';
import SlidingSelect from '@components/recipe/recipe-detail/SlidingSelect';
import ShoppableTools from '@views/RecipeDetailPage/_ShoppableTools';
import IngredientList from '@views/RecipeDetailPage/_IngredientList';

interface Props {
  loading?: boolean;
  servingSizes: Array<{ people: number }>;
  servingSize: number;
  unitOfMeasure: number;
  setUnitOfMeasure: StateSetterFunction<number>;
  ingredientSummary?: IngredientSummaryProps;
  isLoading: boolean;
  className?: string;
  shoppableTools: any;
  shoppableIngredients: Array<any>;
}

const RecipeIngredients = ({
  servingSizes,
  servingSize,
  unitOfMeasure,
  setUnitOfMeasure,
  ingredientSummary,
  isLoading,
  className,
  shoppableTools,
}: Props) => {
  const { t } = useTranslation();
  if (!servingSizes?.length || !ingredientSummary) return null;

  return (
    <div className={className}>
      {(isLoading || !!servingSize) && (
        <>
          <h6 id="serving-people" className="tra-text-web-heading-6 tw-mb-7">
            {t('recipeContent.yieldsLabel', undefined, { default: 'Yields' })}:{' '}
            <span>{servingSize}</span>{' '}
            {t('recipeContent.servingsLabel', undefined, { default: 'Servings' })}
          </h6>
          <hr />
        </>
      )}

      <h4 className="tra-text-web-heading-4 tw-text-left sm:tw-pb-5 tw-mt-8">
        {t('recipeContent.ingredientsLabel', undefined, { default: 'Ingredients' })}
      </h4>

      {ingredientSummary?.groups?.map((group, i) => (
        <IngredientList group={group} unitOfMeasure={unitOfMeasure} key={i} />
      ))}
      {!!shoppableTools?.products?.length && <ShoppableTools shoppableTools={shoppableTools} />}
      {ingredientSummary?.hasMetric && ingredientSummary?.hasImperial && (
        <div className="tw-flex tw-flex-row tw-mt-3">
          <div className="tw-w-full tw-mb-10">
            <h5 className="tw-text-left tw-mb-4 tra-text-web-heading-7 tw-w-full">
              {t('recipeContent.unitsOfMeasurementLabel', undefined, {
                default: 'Units of Measurement',
              })}
              :
            </h5>
            <SlidingSelect
              options={[
                {
                  text: t('unitsOfMeasurement.imperialLabel', undefined, { default: 'Imperial' }),
                  value: UnitOfMeasureEnum.Imperial,
                },
                {
                  text: t('unitsOfMeasurement.metricLabel', undefined, { default: 'Metric' }),
                  value: UnitOfMeasureEnum.Metric,
                },
              ]}
              selectedValue={unitOfMeasure}
              setSelected={(value) => setUnitOfMeasure(value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeIngredients;
